import { useEffect, useState, type KeyboardEvent } from "react";

interface NumberInputProps {
  value: number;
  onChange: (n: number) => void;
  className?: string;
  min?: number;
  max?: number;
  placeholder?: string;
  ariaLabel?: string;
}

// Campo numérico que SÍ permite vaciarse. El problema del "0 pegado" venía de
// inputs controlados con value={numero} + onChange que reconvertía "" a 0, así
// que nunca se podía dejar el campo en blanco. Aquí se maneja el texto aparte y
// solo se emite el número (0 cuando está vacío) sin volver a escribir el "0".
const NumberInput = ({
  value,
  onChange,
  className,
  min,
  max,
  placeholder,
  ariaLabel,
}: NumberInputProps) => {
  const [text, setText] = useState(value ? String(value) : "");
  const [focused, setFocused] = useState(false);

  // Sincroniza cuando el valor cambia desde afuera (ej. sugerencias, reseteos),
  // pero nunca mientras el usuario está escribiendo.
  useEffect(() => {
    if (focused) return;
    setText(value ? String(value) : "");
  }, [value, focused]);

  const handleChange = (raw: string) => {
    const clean = raw.replace(/[^0-9.-]/g, "");
    setText(clean);
    if (clean === "" || clean === "-" || clean === ".") {
      onChange(0);
      return;
    }
    let n = Number(clean);
    if (Number.isNaN(n)) n = 0;
    if (typeof min === "number" && n < min) {
      onChange(n);
      return;
    }
    if (typeof max === "number" && n > max) {
      onChange(max);
      setText(String(max));
      return;
    }
    onChange(n);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "e" || e.key === "E" || e.key === "+") e.preventDefault();
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setText(value ? String(value) : "");
      }}
      onChange={(e) => handleChange(e.target.value)}
      onKeyDown={handleKeyDown}
      className={className}
    />
  );
};

export default NumberInput;